import { isEmpty, minBy } from "lodash";

import {
  StripePriceDto,
  StripePriceModel,
  StripePriceType,
  ValidatePatientQuestionAnswerModel
} from "graphql/generated";
import { EvalFlowPriceSelectionVariant } from "src/types";
import {
  extractNumberFromText,
  getTransactionFee,
  getUnitAmountByRegion,
  roundPrice
} from "src/utils";

export const getDisplayVariationText = (variant: string, display: string): string[] => {
  let displayTexts: string[] = [];

  switch (variant) {
    case "dosage":
    case "frequency":
      displayTexts = display?.split("\n") || [];
      break;

    default:
      displayTexts[0] = display;
  }

  displayTexts[0] = displayTexts?.[0]?.trim() || "";
  displayTexts[1] = displayTexts?.[1]?.trim() || "";

  return displayTexts;
};

export const getBoxQuantityFromPrice = (priceVariant: Pick<StripePriceDto, "metadata">) => {
  let boxQuantity = +priceVariant.metadata.boxes;

  // some price don't have boxes
  if (!boxQuantity) {
    boxQuantity = extractNumberFromText(priceVariant.metadata.frequency_quantity_display);
  }

  return boxQuantity;
};

export const calculatePricePerBoxFromVariant = (
  priceVariant: Pick<StripePriceDto, "unit_amount" | "metadata">
): number => {
  if (!priceVariant) {
    return null;
  }

  const unitAmount = getUnitAmountByRegion(priceVariant.unit_amount);
  const boxQuantity = getBoxQuantityFromPrice(priceVariant);

  const basePricePerBox = unitAmount / boxQuantity;

  return basePricePerBox;
};

export const calculateBundleSavePercent = (
  basePricePerBox: number,
  priceVariant: Pick<StripePriceDto, "unit_amount">
): number => {
  if (!basePricePerBox || !priceVariant) {
    return null;
  }

  const pricePerBox = calculatePricePerBoxFromVariant(priceVariant);

  const percent = ((basePricePerBox - pricePerBox) / basePricePerBox) * 100;

  const roundedPercent = Math.floor(percent);

  return roundedPercent;
};

export const calculateStrikeoutPrice = (
  basePricePerBox: number,
  priceVariant: Pick<StripePriceDto, "metadata">
) => {
  if (!basePricePerBox || !priceVariant) {
    return null;
  }

  const boxQuantity = getBoxQuantityFromPrice(priceVariant);

  const strikeoutPrice = basePricePerBox * boxQuantity;

  const strikeoutWithoutFee = strikeoutPrice - getTransactionFee(strikeoutPrice);

  return roundPrice(strikeoutWithoutFee);
};

export const getBasePricePerBoxFromVariants = (
  priceVariants: Pick<StripePriceDto, "unit_amount">[]
): number => {
  if (isEmpty(priceVariants)) {
    return null;
  }

  const basePriceVariant = minBy(priceVariants, "unit_amount");

  const basePricePerBox = calculatePricePerBoxFromVariant(basePriceVariant);

  return basePricePerBox;
};

export const filterActivePrices = (prices: StripePriceModel[]): StripePriceModel[] => {
  return prices?.filter((p) => p.active && p.metadata?.isArchived !== "true");
};

export const checkIsFixedVariant = (
  prices: { metadata?: any }[],
  variant: "dosage" | "frequency"
): boolean => {
  const activePrices = filterActivePrices(prices);

  if (isEmpty(activePrices)) {
    return false;
  }

  const firstVariantValue = activePrices[0].metadata?.[variant];

  // fixed variant usually don't have meta
  if (!firstVariantValue) {
    return true;
  }

  const isContainOtherVariants = activePrices.some((p) => {
    if (p.metadata?.[variant] && p.metadata?.[variant] !== firstVariantValue) {
      return true;
    }

    return false;
  });

  return !isContainOtherVariants;
};

/**
 * OR-3374
 * if pt selecting dosage -> dosage is various -> frequency must be fixed
 * therefore when finish selecting dosage, the frequency stil keep the same with preselect
 * @return null: not enough info to check -> skip
 */
export const checkPriceMetaMatchPreSelectMeta = (
  curPrice: { metadata?: any },
  preSelectePrice: StripePriceModel,
  currentVariant: EvalFlowPriceSelectionVariant
) => {
  if (!curPrice || !preSelectePrice) {
    return null;
  }

  const needFilterVariant: EvalFlowPriceSelectionVariant =
    currentVariant === "dosage" ? "frequency" : "dosage";

  const preSelectVariantValue = +preSelectePrice.metadata?.[needFilterVariant];
  const curPriceVariantValue = +curPrice.metadata?.[needFilterVariant];

  if (!preSelectVariantValue || !curPriceVariantValue) {
    return null;
  }

  return curPriceVariantValue === preSelectVariantValue;
};

export const getFilteredPriceType = (stripePrices: StripePriceModel[]): StripePriceType => {
  if (isEmpty(stripePrices)) {
    return null;
  }

  const hasRecurPrice = stripePrices.some((price) => price.type === StripePriceType.Recurring);

  const filteredPriceType = hasRecurPrice ? StripePriceType.Recurring : StripePriceType.OneTime;

  return filteredPriceType;
};

export const mapPricesWithTransMeta = (
  stripePrices: StripePriceModel[],
  priceTranslatedTexts: {
    translatedTexts: any;
    priceId: string;
  }[]
): StripePriceModel[] => {
  if (isEmpty(stripePrices) || isEmpty(priceTranslatedTexts)) {
    return [];
  }

  const filteredPriceType = getFilteredPriceType(stripePrices);

  const mappedPriceWithTransMeta: StripePriceModel[] = stripePrices.reduce((prev, cur) => {
    if (!cur.active || cur.metadata?.isArchived === "true" || cur.type !== filteredPriceType) {
      return prev;
    }

    const metadataInCodebase: Object = priceTranslatedTexts?.find(
      (p) => p.priceId === cur.id
    )?.translatedTexts;

    return [...prev, { ...cur, metadata: { ...cur.metadata, ...metadataInCodebase } }];
  }, []);

  return mappedPriceWithTransMeta;
};

export const getPricesBasedOnAnswer = (
  valPtQuesAns: ValidatePatientQuestionAnswerModel[],
  stripePrices: StripePriceModel[]
): StripePriceModel[] => {
  if (isEmpty(valPtQuesAns) || isEmpty(stripePrices)) {
    return [];
  }

  // current only have 1 ques/eval
  const preselectQuesAns = valPtQuesAns.find((vpqa) => vpqa.validation?.preselect);

  if (isEmpty(preselectQuesAns)) {
    return [];
  }

  const patientPreselectAnswer = preselectQuesAns.patientQuestionAnswer.value;
  const preselectType = preselectQuesAns.validation.preselect;

  const filteredPrices = stripePrices.filter(
    (p) => +p.metadata?.[preselectType] === +patientPreselectAnswer
  );

  return filteredPrices;
};

/**
 * Logic:
 * - OR-3791 will filter all prices that match patient answer
 * - Get price with popular dose AND freq
 * - If not have, get price with popular dose
 * - If not have, get price with popular freq
 * - If still can't find, just pick the first one
 * @params stripe prices of product
 * @params priceTranslatedTexts: remember to import all price trans files
 * @return priceId
 */
export const getMostPopularPriceId = (
  stripePrices: StripePriceModel[],
  priceTranslatedTexts: {
    translatedTexts: any;
    priceId: string;
  }[],
  valPtQuesAns?: ValidatePatientQuestionAnswerModel[]
): string => {
  if (isEmpty(stripePrices) || isEmpty(priceTranslatedTexts)) {
    return null;
  }

  const pricesBasedOnAns = getPricesBasedOnAnswer(valPtQuesAns, stripePrices);

  const finalPrices = isEmpty(pricesBasedOnAns) ? stripePrices : pricesBasedOnAns;

  const mappedPriceWithTransMeta = mapPricesWithTransMeta(finalPrices, priceTranslatedTexts);

  let popularPriceId = "";
  let shouldGetByFreq = true;

  mappedPriceWithTransMeta.every((p) => {
    // sometime true, sometime "true"
    const isFreqPopular = !!(
      p.metadata?.frequency_popular && p.metadata?.frequency_popular === "true"
    );
    const isDosPopular = !!(p.metadata?.dosage_popular && p.metadata?.dosage_popular === "true");

    if (isDosPopular && isFreqPopular) {
      popularPriceId = p.id;
      return;
    }

    if (isDosPopular) {
      popularPriceId = p.id;
      shouldGetByFreq = false;
    }

    // only choose by freq if never has isDosPopular
    else if (shouldGetByFreq && isFreqPopular) {
      popularPriceId = p.id;
    }

    // continue loop
    return true;
  });

  if (!popularPriceId) {
    popularPriceId = mappedPriceWithTransMeta[0].id;
  }

  return popularPriceId;
};
